/**
 * ONE ERROR TYPE, over the SHARED `ErrorBody`.
 *
 * A client that throws `new Error("500 /path")` gives nothing anything can branch on: not the retry policy, not
 * the "you are being rate limited, here is when to try again" line, not the degraded banner. So every failure
 * that leaves `api/http.ts` is an `ApiError` carrying the server's own machine-readable `code` — the same
 * `ErrorCode` union the server's `errors.ts` declares, proven identical by `packages/wire`'s conformance
 * test — plus the two codes the SERVER CANNOT PRODUCE because they happen before or instead of a response.
 */

import type { ErrorBody, ErrorCode, TradeErrorCode } from "@kinetiq/wire";

/**
 * The codes that never come from a server.
 *
 * They are spelled distinctly from the server's set on purpose: a `timeout` and a `503 not_ready` need
 * different words in the UI ("we could not reach the server" vs "the server is still catching up"), and
 * collapsing them is how a client-side network problem gets reported as a server outage.
 */
export const CLIENT_ERROR_CODES = [
  "network",
  "timeout",
  "aborted",
  "malformed",
  "no_session",
] as const;
export type ClientErrorCode = (typeof CLIENT_ERROR_CODES)[number];

/**
 * ⚠️ **BOTH SERVERS' CODE SETS, because this client calls both through one `request()`.**
 *
 * `ErrorBody.code` on the wire is typed `ErrorCode` and its own comment calls it "ONE `ErrorBody` for both
 * servers" — but it is not: the trader surface declares its own `TradeErrorCode`, and four of its members
 * (`unauthorized`, `forbidden`, `precondition_required`, `vendor_refused`, `budget_exhausted`) are simply
 * absent from `ErrorCode`. `errorFromBody` below has always CAST the parsed code, so those values have been
 * arriving here at runtime the whole time while the type said they could not exist.
 *
 * That was harmless only while nothing branched on them. It stopped being harmless with resting orders: a
 * `428 precondition_required` means "re-authorise your vault" and a `429 rate_limited` means "wait N
 * seconds", and both need a branch. Widening the union here is the honest description of what this type
 * holds; the real fix — one code union on the wire, or two error types with two `ErrorBody`s — is a wire
 * change and is filed in `todo.md`.
 */
export type AnyErrorCode = ErrorCode | TradeErrorCode | ClientErrorCode;

export class ApiError extends Error {
  readonly code: AnyErrorCode;
  /** The HTTP status, or `0` when no response was received. `0` and a code of `network`/`timeout`/`aborted`
   *  are the same fact stated twice, deliberately: a caller switching on status does not silently treat a
   *  network failure as a `200`. */
  readonly status: number;
  /** 400 only: the NAME of the offending parameter. Never its value — the server does not echo it, and a
   *  client that reconstructs it turns an error body into a reflector. */
  readonly field: string | undefined;
  readonly accepted: readonly string[] | undefined;
  /** Seconds, on 429 and 503. */
  readonly retryAfter: number | undefined;
  /** A passed-through vendor 4xx. Its message is shown VERBATIM — "Order must be at least 10 USD (current
   *  value: 0.08 USD)" is the most useful thing we can say, and paraphrasing it loses the number. */
  readonly source: ErrorBody["source"] | undefined;
  /** The request that failed, for the log and the beacon. NEVER a body — those carry signed transactions. */
  readonly path: string;

  constructor(init: {
    code: AnyErrorCode;
    message: string;
    status?: number;
    path: string;
    field?: string;
    accepted?: readonly string[];
    retryAfter?: number;
    source?: ErrorBody["source"];
  }) {
    super(init.message);
    this.name = "ApiError";
    this.code = init.code;
    this.status = init.status ?? 0;
    this.path = init.path;
    this.field = init.field;
    this.accepted = init.accepted;
    this.retryAfter = init.retryAfter;
    this.source = init.source;
  }

  /** Is retrying this ON ITS OWN worth anything? Used by the fetch wrapper's GET policy and by the stitch's
   *  seed retry, so the two cannot disagree about what "transient" means.
   *
   *  `rate_limited` is deliberately NOT here. A 429 is retried by the CONTROLLER on its own cadence with
   *  `Retry-After` respected, never by the transport: auto-retrying inside the wrapper triples the requests
   *  under exactly the condition the bucket exists for. */
  get transient(): boolean {
    return (
      this.code === "network" ||
      this.code === "timeout" ||
      this.code === "not_ready" ||
      GATEWAY_STATUSES.has(this.status ?? 0)
    );
  }
}

/** Narrow an unknown catch. `instanceof` alone is unreliable across a bundle boundary, so the name is the
 *  belt — but the check is written once, here, rather than at every call site. */
export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError || (e instanceof Error && e.name === "ApiError");
}

/**
 * ⚠️ **THE REFUSALS THAT PROVABLY HAPPENED BEFORE THE VENDOR WAS CALLED** — the one question the money paths
 * ask of an error, so it is answered once, here, and never as a literal at a catch site.
 *
 * On `/order/create` and `/order/:id/cancel/confirm` the client has already handed over a SIGNED transaction,
 * so "did this reach Jupiter?" decides between two very different sentences: "nothing moved, try again" and
 * "we do not know whether your money is in a vault". Getting it wrong in the safe direction is not free — the
 * loud unknown offers no retry and tells the user to go and check their activity, which is a false alarm on
 * the screen that can least afford one.
 *
 * Every code below is emitted by a server line that returns BEFORE the handler's vendor call, and the list is
 * exhaustive of those, read from the server rather than recalled:
 *
 *   · `unauthorized` / `rate_limited` — `apps/api/src/user/router.ts`: the per-client bucket, the auth
 *     guard and the per-principal cadence all return before `def.handler` is invoked.
 *   · `budget_exhausted` — `spend(r.env)` in `routes/orders.ts`, the statement immediately before the `try`
 *     that calls the vendor, on BOTH the create and the cancel-confirm. **This is the one that was missing**,
 *     and it is the likeliest of the set to fire in production: it is the SHARED 50 RPS Jupiter budget that
 *     every quote on the box also draws from, so a busy minute — not a broken client — produces it.
 *   · `precondition_required` — `vendorFor`'s "no Jupiter authorisation on file" 428, and the vault gate.
 *   · `forbidden` / `not_found` — the intent-ownership checks, which the route runs before the spend on
 *     purpose so a refusal costs neither a vendor request nor a stranger's alarm.
 *   · `bad_request` — `parse()` / `badField`, before anything else in the handler.
 *   · `no_session` — ours: the request never left the browser.
 *
 * A VENDOR-SIDE refusal is deliberately NOT here: a Jupiter 4xx (including a Jupiter 429) comes back as
 * `vendor_refused` precisely so it cannot be confused with our own limits, and by then the deposit was handed
 * over. Nor are `network` / `timeout` / `aborted` / `malformed`: a request that failed in flight may have been
 * delivered, and that is the definition of the unknown.
 */
export const PRE_VENDOR_ERROR_CODES: readonly AnyErrorCode[] = [
  "no_session",
  "unauthorized",
  "forbidden",
  "not_found",
  "bad_request",
  "precondition_required",
  "rate_limited",
  "budget_exhausted",
] as const;

const PRE_VENDOR = new Set<string>(PRE_VENDOR_ERROR_CODES);

/** Did this refusal happen before our server could reach the vendor? `false` for anything that is not an
 *  `ApiError` at all — an unrecognised throw is exactly the case that must stay unknown. */
export function isPreVendorRefusal(e: unknown): e is ApiError {
  return isApiError(e) && PRE_VENDOR.has(e.code);
}

/** The two 429s. Same recovery — wait the server's own `Retry-After` — but different causes, so the copy
 *  differs and the branch is written once here rather than guessed at three call sites. */
export function isRateLimit(e: unknown): e is ApiError {
  return isApiError(e) && (e.code === "rate_limited" || e.code === "budget_exhausted");
}

/**
 * THE STATUSES AN INTERMEDIARY PRODUCES ON ITS OWN. Retryable, and — critically — the ones whose bodies we
 * may never get to see. Kept as one set so `transient` and `errorFromBody` cannot disagree about which
 * statuses mean "a hop failed", which is how 504 came to be retryable in one and not the other.
 */
const GATEWAY_STATUSES = new Set([502, 503, 504]);

/**
 * Parse a `!ok` response body into an `ApiError`.
 *
 * A body that is NOT the shared `ErrorBody` becomes `malformed` rather than being coerced: coercing invents
 * a `code` the server never sent, and a client that branches on an invented code branches wrongly. That rule
 * stands. What changed is the MESSAGE, because the code and the message answer different questions.
 *
 * ⚠️ AN UNPARSEABLE BODY IS THE NORMAL CASE FOR A 5xx, NOT AN EXOTIC ONE. MEASURED 2026-08-14 against the
 * live site: our origin answers `/user/v1/auth/otp/start` with a perfectly good
 * `{"error":…,"code":"serve_failed"}` at 502, and **Cloudflare replaces it** with its own `text/plain` page
 * reading `error code: 502`. nginx's stock pages do the same for a 504. So on every gateway status the
 * server's own words are simply not available to us — no client change can recover them.
 *
 * Which means the old message — "the server returned 502 with a body this client does not recognise" —
 * described OUR PARSING PROBLEM to a user trying to sign in. It is accurate and useless: it names no cause
 * they can act on and implies a client bug that is not there. The STATUS is still evidence, so we say what
 * the status means and stay silent about the rest.
 */
export function errorFromBody(
  status: number,
  path: string,
  body: unknown,
  retryAfterHeader: number | null,
): ApiError {
  const b = body as Partial<ErrorBody> | null;
  const known = typeof b?.error === "string" && typeof b?.code === "string";
  if (!known) {
    return new ApiError({
      // The CODE stays honest — we genuinely did not recognise this body, and `malformed` says exactly that
      // without claiming the server spoke. Only the sentence is written for a human.
      code: status === 429 ? "rate_limited" : "malformed",
      message: GATEWAY_STATUSES.has(status)
        ? "the trading service is temporarily unavailable — please try again in a moment"
        : status >= 500
          ? "something went wrong on our side — please try again"
          : `the server returned ${status} with a body this client does not recognise`,
      status,
      path,
      ...(retryAfterHeader !== null ? { retryAfter: retryAfterHeader } : {}),
    });
  }
  return new ApiError({
    code: b.code as ErrorCode,
    message: b.error as string,
    status,
    path,
    ...(b.field !== undefined ? { field: b.field } : {}),
    ...(b.accepted !== undefined ? { accepted: b.accepted } : {}),
    // The header wins over the body only when the body has none: the body is the authority the server
    // documented, and `Retry-After` can be rewritten by a proxy in between.
    ...(b.retryAfter !== undefined
      ? { retryAfter: b.retryAfter }
      : retryAfterHeader !== null
        ? { retryAfter: retryAfterHeader }
        : {}),
    ...(b.source !== undefined ? { source: b.source } : {}),
  });
}
